import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, Search, Users, UserCheck, ClipboardList, Calendar as CalendarIcon, Bell, Zap, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Checkbox } from '../../ui/checkbox';
import { Calendar } from '../../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { db } from '../../../lib/db';
import { useAuthStore } from '../../../store/authStore';
import { useAppraisals, useTemplates } from '../../../hooks/useDatabase';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { v4 as uuidv4 } from 'uuid';

export default function CEOAssignAppraisal() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const { createAppraisal } = useAppraisals();
  const { getTemplates } = useTemplates();
  
  const [users, setUsers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [dueDate, setDueDate] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [cycleDays, setCycleDays] = useState('none');
  const [autoReminders, setAutoReminders] = useState(false);
  const [reminderLeadDays, setReminderLeadDays] = useState('none');
  
  // Direct mode - appraisal finalizes immediately after employee completes
  const [directMode, setDirectMode] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersData, templatesData] = await Promise.all([
        db.users.toArray(),
        getTemplates()
      ]);
      
      // Filter out admin and CEO, only show supervisors and employees
      setUsers(usersData.filter(u => 
        u.status !== 'deleted' && 
        ['supervisor', 'employee'].includes(u.role)
      ));
      setTemplates(templatesData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const supervisors = users.filter(u => u.role === 'supervisor');
  const employees = users.filter(u => u.role === 'employee');
  
  // Get unique departments
  const departments = [...new Set(users.map(u => u.profile?.department).filter(Boolean))];

  // When direct mode is enabled, only show employees
  const availableUsers = directMode ? employees : users;

  const filteredUsers = availableUsers.filter(u => {
    const matchesSearch = searchQuery === '' ||
      u.profile?.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.profile?.lastName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesRole = roleFilter === 'all' || u.role === roleFilter;
    const matchesDepartment = departmentFilter === 'all' || u.profile?.department === departmentFilter;
    
    return matchesSearch && matchesRole && matchesDepartment;
  });

  const toggleDirectMode = () => {
    setDirectMode(!directMode);
    setSelectedUsers([]); // Clear selection when switching modes
    setRoleFilter('all');
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const selectAll = () => {
    const allFilteredIds = filteredUsers.map(u => u.id);
    setSelectedUsers(prev => {
      const newSelection = [...prev];
      allFilteredIds.forEach(id => {
        if (!newSelection.includes(id)) {
          newSelection.push(id);
        }
      });
      return newSelection;
    });
  };

  const clearSelection = () => {
    setSelectedUsers([]);
  };

  const handleTemplateChange = (templateId) => {
    setSelectedTemplate(templateId);
  };

  const handleAssign = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Please select at least one user');
      return;
    }
    if (!selectedTemplate) {
      toast.error('Please select a template');
      return;
    }
    if (!dueDate) {
      toast.error('Please select a due date');
      return;
    }

    setSubmitting(true);

    try {
      const template = templates.find(t => t.id === selectedTemplate);
      const performanceAreas = template?.sections?.[0]?.competencies?.map(c => ({
        competencyId: c.id,
        competencyName: c.name,
        weight: c.weight,
        completedByEmployee: false,
        completedByManager: false
      })) || [];

      let successCount = 0;

      for (const userId of selectedUsers) {
        const targetUser = users.find(u => u.id === userId);

        // Determine flow based on mode
        let managerId = null;
        let visibleToSupervisor = true;
        let directFinalize = false;

        if (directMode) {
          // Direct mode: No supervisor, finalizes after employee completes
          managerId = null;
          visibleToSupervisor = false;
          directFinalize = true;
        } else {
          // Normal flow: Employee -> Supervisor -> (optionally CEO)
          managerId = targetUser?.supervisorId || null;
          visibleToSupervisor = true;
          directFinalize = false;
        }

        const appraisalData = {
          templateId: selectedTemplate,
          userId: userId,
          managerId: managerId,
          title: template?.name || 'Performance Appraisal',
          type: template?.type || 'annual',
          status: 'ready_to_evaluate',
          dueDate: dueDate.getTime(),
          performanceAreas: performanceAreas,
          assignedByCeo: user?.id,
          ceoId: user?.id,
          visibleToSupervisor: visibleToSupervisor,
          directFinalize: directFinalize,
          cycleDays: cycleDays && cycleDays !== 'none' ? Number(cycleDays) : null,
          autoReminders: autoReminders,
          reminderLeadDays: autoReminders && reminderLeadDays && reminderLeadDays !== 'none' ? Number(reminderLeadDays) : null
        };

        await createAppraisal(appraisalData);
        successCount++;
      }

      toast.success(`Successfully assigned ${successCount} appraisal(s)`);
      navigate('/ceo/appraisals');
    } catch (error) {
      console.error('Error assigning appraisals:', error);
      toast.error('Failed to assign appraisals');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><span className="text-gray-500">Loading...</span></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Assign Appraisal</h1>
        <p className="text-gray-500 mt-1">Assign performance appraisals to supervisors and employees</p>
      </div>

      {/* Mode Selection */}
      <Button
        variant={directMode ? "default" : "outline"}
        onClick={toggleDirectMode}
        className={directMode ? "bg-emerald-500 hover:bg-emerald-600" : ""}
      >
        <Zap className="w-4 h-4 mr-2" />
        Direct Mode
      </Button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* User Selection */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-indigo-500" />
                Select {directMode ? 'Employees' : 'Users'}
              </CardTitle>
              <CardDescription>
                {directMode 
                  ? 'Choose employees for direct appraisal'
                  : 'Choose supervisors and/or employees to assign appraisals'
                }
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Search & Filter */}
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search by name or email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={roleFilter} onValueChange={setRoleFilter} disabled={directMode}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Filter by role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    <SelectItem value="supervisor">Supervisors</SelectItem>
                    <SelectItem value="employee">Employees</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filter by department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Departments</SelectItem>
                    {departments.map(dept => (
                      <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Selection Actions */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={selectAll}>Select All</Button>
                  <Button variant="outline" size="sm" onClick={clearSelection}>Clear</Button>
                </div>
                <Badge variant="secondary">{selectedUsers.length} selected</Badge>
              </div>

              {/* User List */}
              <div className="max-h-[400px] overflow-y-auto space-y-2 border rounded-lg p-2">
                {filteredUsers.length > 0 ? (
                  filteredUsers.map(u => (
                    <div
                      key={u.id}
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                        selectedUsers.includes(u.id)
                          ? 'bg-indigo-50 border-2 border-indigo-300'
                          : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                      }`}
                      onClick={() => toggleUserSelection(u.id)}
                    >
                      <Checkbox
                        checked={selectedUsers.includes(u.id)}
                        onCheckedChange={() => toggleUserSelection(u.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="w-10 h-10 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full flex items-center justify-center text-white font-medium text-sm">
                        {u.profile?.firstName?.[0]}{u.profile?.lastName?.[0]}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">
                          {u.profile?.firstName} {u.profile?.lastName}
                        </p>
                        <p className="text-sm text-gray-500">{u.profile?.position} • {u.profile?.department}</p>
                      </div>
                      <Badge variant={u.role === 'supervisor' ? 'default' : 'secondary'} className="capitalize">
                        {u.role}
                      </Badge>
                    </div>
                  ))
                ) : (
                  <p className="text-center text-gray-500 py-8">No users found</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Appraisal Configuration */}
        <div className="space-y-4">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-indigo-500" />
                Appraisal Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Template Selection */}
              <div className="space-y-2">
                <Label>Template *</Label>
                <Select value={selectedTemplate} onValueChange={handleTemplateChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a template" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map(t => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Due Date */}
              <div className="space-y-2">
                <Label>Due Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dueDate ? format(dueDate, 'PPP') : 'Select due date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={dueDate}
                      onSelect={setDueDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Cycle & Reminders */}
              <div className="space-y-4 pt-4 border-t">
                {/* Cycle */}
                <div className="space-y-2">
                  <Label>Cycle (optional)</Label>
                  <Select value={cycleDays} onValueChange={setCycleDays}>
                    <SelectTrigger>
                      <SelectValue placeholder="No cycle" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No cycle</SelectItem>
                      <SelectItem value="7">Every 7 days</SelectItem>
                      <SelectItem value="15">Every 15 days</SelectItem>
                      <SelectItem value="30">Every 30 days</SelectItem>
                      <SelectItem value="90">Every 90 days (Quarterly)</SelectItem>
                      <SelectItem value="180">Every 180 days (Semi-Annual)</SelectItem>
                      <SelectItem value="365">Every 365 days (Annual)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Auto Reminders */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="autoReminders"
                      checked={autoReminders}
                      onCheckedChange={(checked) => {
                        setAutoReminders(!!checked);
                        if (!checked) setReminderLeadDays('none');
                      }}
                    />
                    <Label htmlFor="autoReminders" className="text-sm flex items-center gap-1">
                      <Bell className="w-3 h-3" /> Enable auto reminders
                    </Label>
                  </div>

                  <Select
                    disabled={!autoReminders}
                    value={reminderLeadDays}
                    onValueChange={setReminderLeadDays}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select timing" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select timing</SelectItem>
                      <SelectItem value="1">1 day before due</SelectItem>
                      <SelectItem value="3">3 days before due</SelectItem>
                      <SelectItem value="7">7 days before due</SelectItem>
                      <SelectItem value="14">14 days before due</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Summary & Submit */}
          <Card className="border-0 shadow-sm bg-gradient-to-br from-indigo-50 to-purple-50">
            <CardContent className="pt-6">
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Users Selected:</span>
                  <span className="font-medium">{selectedUsers.length}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Template:</span>
                  <span className="font-medium">
                    {templates.find(t => t.id === selectedTemplate)?.name || 'Not selected'}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Due Date:</span>
                  <span className="font-medium">
                    {dueDate ? format(dueDate, 'MMM d, yyyy') : 'Not set'}
                  </span>
                </div>
              </div>

              <Button
                className="w-full mt-6 bg-emerald-600 hover:bg-emerald-700"
                onClick={handleAssign}
                disabled={submitting || selectedUsers.length === 0 || !selectedTemplate || !dueDate}
              >
                {submitting ? (
                  'Assigning...'
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Assign Appraisal{selectedUsers.length > 1 ? 's' : ''}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
